import { Component, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyService } from '../../services/CompanyService/company';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

export interface DisableUserDialogData {
  userId: string;
  userEmail: string;
  /**
   * Which endpoint to disable through. Both record the reason the same way; they differ only in
   * who is allowed to call them — 'platform' is superadmin, 'company' is a company admin acting
   * on their own staff. Defaults to 'platform' so existing callers are unaffected.
   */
  scope?: 'platform' | 'company';
}

@Component({
  selector: 'app-disable-user-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './disable-user-dialog.html',
  styleUrl: './disable-user-dialog.scss',
})
export class DisableUserDialog {
  reason = '';
  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(
    private dialogRef: MatDialogRef<DisableUserDialog>,
    private admin: AdminService,
    private company: CompanyService,
    @Inject(MAT_DIALOG_DATA) public data: DisableUserDialogData
  ) {}

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.reason.trim()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const request = { reason: this.reason };
    const action =
      this.data.scope === 'company'
        ? this.company.disableUser(this.data.userId, request)
        : this.admin.disableUser(this.data.userId, request);

    action.subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to disable user.'));
      },
    });
  }
}
